import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Button, Card, CardBody, Col, Container, FormGroup, Input, Label,
    Row, Spinner, Table
} from "reactstrap";
import { FaChartBar, FaChevronLeft, FaChevronRight } from "react-icons/fa6";
import { FaFileExcel, FaFilePdf } from "react-icons/fa";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { formatPrice } from "../../helpers/utility";
import { commercialActions } from "../../sagas/commercialSlice";

// Recharts (synthèse Top performers)
import {
    ResponsiveContainer, BarChart, Bar, CartesianGrid, XAxis, YAxis, Tooltip, Legend
} from "recharts";

const getMonthRange = (monthDate) => {
    const year = monthDate.getFullYear();
    const month = monthDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const now = new Date();
    const isCurrentMonth = year === now.getFullYear() && month === now.getMonth();
    const endDate = isCurrentMonth ? now : lastDay;

    return {
        dateFrom: firstDay.toISOString().slice(0, 10),
        dateTo: endDate.toISOString().slice(0, 10),
    };
};

const getMonthLabel = (monthDate) =>
    new Intl.DateTimeFormat("fr-FR", { month: "long", year: "numeric" }).format(monthDate);

export default function CommercialProgressPage() {
    const dispatch = useDispatch();

    const { list: commercials, loading } = useSelector((s) => s.commercials);
    const { performanceById, perfLoading } = useSelector((s) => s.commercials);

    const [selectedMonth, setSelectedMonth] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1));

    const [perfParams, setPerfParams] = useState(() => getMonthRange(new Date()));
    const [baseRatePct, setBaseRatePct] = useState(1);
    const baseRateDecimal = Number(baseRatePct || 0) / 100;

    // Charger commerciaux + perf
    useEffect(() => {
        if (!commercials || commercials.length === 0) {
            dispatch(commercialActions.list({}));
        }
    }, []);

    useEffect(() => {
        dispatch(commercialActions.loadPerformance({
            startDate: perfParams.dateFrom,
            endDate: perfParams.dateTo,
            baseRate: baseRateDecimal,
        }));
    }, [perfParams.dateFrom, perfParams.dateTo, baseRatePct]);

    const shiftMonth = (delta) => {
        setSelectedMonth((previous) => {
            const nextMonth = new Date(previous.getFullYear(), previous.getMonth() + delta, 1);
            setPerfParams(getMonthRange(nextMonth));
            return nextMonth;
        });
    };

    // Dataset pour tableau
    const rows = useMemo(() => {
        if (!commercials) return [];
        return commercials.map((c) => {
            const p = performanceById?.[c.id];
            const objective = Number(p?.objective || 0);
            const baseTotal = Number(p?.baseTotal || 0);
            const pct = objective > 0 ? Math.min(100, Math.round((baseTotal / objective) * 100)) : 0;
            return {
                id: c.id,
                code: c.code,
                fullName: c.fullName,
                phone: c.phone,
                contractType: c.contractType,
                plan: p?.plan || null,
                objective,
                baseTotal,
                pct,
                totalCommission: Number(p?.totalCommission || 0),
                baseUsed: p?.baseUsed || "-",
            };
        });
    }, [commercials, performanceById]);

    // Synthèse Top performers (POSTED vs preview? Ici: estimation “preview/perf”)
    const chartData = useMemo(() => {
        // Tri par total commission estimée (desc)
        const sorted = [...rows].sort((a, b) => b.totalCommission - a.totalCommission).slice(0, 10);
        return sorted.map(r => ({
            commercial: r.fullName || r.code,
            commission: r.totalCommission,
        }));
    }, [rows]);

    const isBusy = loading || perfLoading;
    const exportRows = rows.map((r) => ({
        Code: r.code || "-",
        Commercial: r.fullName || "-",
        Telephone: r.phone || "-",
        Realise: r.baseTotal || 0,
        CommissionEstimee: r.totalCommission || 0,
    }));

    const handleExportExcel = () => {
        if (!exportRows.length) return;
        const ws = XLSX.utils.json_to_sheet(exportRows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Progression");
        XLSX.writeFile(wb, `Progression_Commerciaux_${perfParams.dateFrom}_${perfParams.dateTo}.xlsx`);
    };

    const handleExportPdf = () => {
        if (!exportRows.length) return;
        const doc = new jsPDF({ orientation: "landscape" });
        doc.setFontSize(12);
        doc.text(`Progression commerciaux - ${getMonthLabel(selectedMonth)}`, 14, 14);
        doc.setFontSize(10);
        doc.text(`Periode: ${perfParams.dateFrom} au ${perfParams.dateTo}`, 14, 20);

        autoTable(doc, {
            startY: 26,
            head: [["Code", "Commercial", "Telephone", "Realise", "Commission estimee"]],
            body: exportRows.map((r) => [
                r.Code,
                r.Commercial,
                r.Telephone,
                formatPrice(r.Realise),
                formatPrice(r.CommissionEstimee),
            ]),
            styles: { fontSize: 9 },
            headStyles: { fillColor: [52, 58, 64] },
        });

        doc.save(`Progression_Commerciaux_${perfParams.dateFrom}_${perfParams.dateTo}.pdf`);
    };

    return (
        <Container fluid className="mt-3">
            <Row className="mb-3">
                <Col>
                    <h4 className="mb-0">Progression des commerciaux</h4>
                    <div className="text-muted">Aperçu des objectifs vs réalisations et commission estimée sur la période sélectionnée.</div>
                </Col>
            </Row>

            {/* Filtres */}
            <Card className="shadow-sm mb-3">
                <CardBody>
                    <Row className="g-3 align-items-end">
                        <Col md={4}>
                            <FormGroup className="mb-0">
                                <Label className="mb-1">Mois</Label>
                                <div className="d-flex align-items-center gap-2">
                                    <Button color="light" onClick={() => shiftMonth(-1)} title="Mois précédent">
                                        <FaChevronLeft />
                                    </Button>
                                    <div className="flex-grow-1 text-center border rounded px-2 py-2 text-capitalize">
                                        {getMonthLabel(selectedMonth)}
                                    </div>
                                    <Button color="light" onClick={() => shiftMonth(1)} title="Mois suivant">
                                        <FaChevronRight />
                                    </Button>
                                </div>
                            </FormGroup>
                        </Col>
                        <Col md={2}>
                            <FormGroup className="mb-0">
                                <Label className="mb-1">Taux base (%)</Label>
                                <Input
                                    type="number"
                                    min={0}
                                    step="0.01"
                                    value={baseRatePct}
                                    onChange={(e) => {
                                        const value = Number(e.target.value);
                                        setBaseRatePct(Number.isFinite(value) && value >= 0 ? value : 0);
                                    }}
                                />
                            </FormGroup>
                        </Col>
                        <Col md={3} className="d-flex align-items-end justify-content-end">
                            <Button
                                color="secondary"
                                onClick={() => dispatch(commercialActions.loadPerformance({
                                    startDate: perfParams.dateFrom,
                                    endDate: perfParams.dateTo,
                                    baseRate: baseRateDecimal,
                                }))}
                                disabled={isBusy}
                            >
                                {isBusy ? <Spinner size="sm" /> : <><FaChartBar className="me-2" />Rafraîchir</>}
                            </Button>
                        </Col>
                        <Col md={3} className="d-flex align-items-end justify-content-end">
                            <Button color="success" className="me-2" onClick={handleExportExcel} disabled={!exportRows.length}>
                                <FaFileExcel className="me-1" />
                                Export Excel
                            </Button>
                            <Button color="danger" onClick={handleExportPdf} disabled={!exportRows.length}>
                                <FaFilePdf className="me-1" />
                                Export PDF
                            </Button>
                        </Col>
                    </Row>
                </CardBody>
            </Card>

            {/* Graphique Top performers */}
            {chartData.length > 0 && (
                <Card className="shadow-sm mb-3">
                    <CardBody>
                        <div className="d-flex align-items-center mb-2">
                            <FaChartBar className="text-primary me-2" />
                            <h5 className="mb-0">Top performers — Commission estimée</h5>
                        </div>
                        <div style={{ width: "100%", height: 320 }}>
                            <ResponsiveContainer>
                                <BarChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 60 }}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis
                                        dataKey="commercial"
                                        interval={0}
                                        angle={-20}
                                        textAnchor="end"
                                        height={70}
                                    />
                                    <YAxis
                                        tickFormatter={(v) =>
                                            v >= 1000000
                                                ? (v / 1000000).toFixed(1) + "M"
                                                : v >= 1000
                                                    ? (v / 1000).toFixed(0) + "k"
                                                    : v
                                        }
                                        width={70}
                                        tick={{ fontSize: 12, fill: "#555" }}
                                    />
                                    <Tooltip
                                        formatter={(value) => formatPrice(value)}
                                        labelFormatter={(label) => `Commercial: ${label}`}
                                    />
                                    <Legend />
                                    <Bar dataKey="commission" name="Commission estimée" />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                        <small className="text-muted">
                            Estimations calculées via l’endpoint batch de performance (non POSTED). Change en temps réel selon les ventes.
                        </small>
                    </CardBody>
                </Card>
            )}

            {/* Tableau progression */}
            <Card className="shadow-sm">
                <CardBody>
                    {isBusy ? (
                        <div className="text-center py-5">
                            <Spinner color="primary" />
                            <div className="mt-2 text-muted">Chargement…</div>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <Table bordered hover>
                                <thead className="table-light">
                                    <tr>
                                        <th>#</th>
                                        <th>Commercial</th>
                                        <th>Réalisé (Total Encaissé)</th>
                                        <th>Commission estimée</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.length > 0 ? rows.map((r, idx) => (
                                        <tr key={r.id}>
                                            <td>{idx + 1}</td>
                                            <td>
                                                <div className="fw-semibold">{r.fullName}</div>
                                                <div className="text-muted small">{r.phone || "-"}</div>
                                            </td>
                                            
                                            <td>{formatPrice(r.baseTotal)}</td>
                                            
                                            <td><strong>{formatPrice(r.totalCommission)}</strong></td>
                                        </tr>
                                    )) : (
                                        <tr>
                                            <td colSpan={7} className="text-center text-muted py-4">Aucun commercial trouvé</td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </CardBody>
            </Card>
        </Container>
    );
}
